import { Fetch } from '../helpers/fetchWrapper';
import {
  adaptActivitiesResponse,
  adaptGraphResponse,
  adaptTopCampaignsResponse,
  buildAnalyticsQueryString,
} from '../helpers/analyticsAdapters';
import { ACTIVE_WORKSPACE_COOKIE, getCookie } from '../helpers/common';
import useServiceQuery from '../hooks/useServiceQuery';

const CACHE_TTL_MS = 60_000;
const responseCache = new Map();

/**
 * Dashboard and Analytics pages request the same charts — dedupe them for a
 * minute, per workspace: switching must not serve the previous one's numbers.
 */
async function withCache(endpointKey, fetcher) {
  const cacheKey = `${getCookie(ACTIVE_WORKSPACE_COOKIE) || ''}:${endpointKey}`;
  const now = Date.now();
  const hit = responseCache.get(cacheKey);
  if (hit && hit.expiresAt > now) {
    return hit.value;
  }

  const value = await fetcher();
  responseCache.set(cacheKey, { value, expiresAt: now + CACHE_TTL_MS });
  return value;
}

const dashboardService = {
  _url:
    process.env.REACT_APP_DASHBOARD_API_URL
    || process.env.REACT_APP_CAMPAIGN_ANALYTICS_API_URL
    || `${process.env.REACT_APP_USER_API_URL}/dashboard`,

  GetTopCampaigns(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.topCampaigns',
      params: searchQuery,
      queryFn: () => this.getTopCampaigns(searchQuery),
      refresh: fetch,
    });
    return {
      top_campaigns_loading: loading,
      top_campaigns_error: error,
      top_campaigns_data: data ?? { topCampaigns: [], totalItems: 0 },
    };
  },

  GetLatestActivities(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.latestActivities',
      params: searchQuery,
      queryFn: () => this.getLatestActivities(searchQuery),
      refresh: fetch,
    });
    return {
      latest_activities_loading: loading,
      latest_activities_error: error,
      latest_activities_data: data ?? { latestActivities: [], totalItems: 0 },
    };
  },

  GetKPIAnalytics(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.kpi',
      params: searchQuery,
      queryFn: () => this.getKpiAnalytics(searchQuery),
      refresh: fetch,
      enabled: Boolean(searchQuery),
    });
    return {
      kpi_loading: loading,
      kpi_error: error,
      kpi_data: data ?? { kpi: [], totalItems: 0 },
    };
  },

  GetGraphData(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.graph',
      params: searchQuery,
      queryFn: () => this.getGraphData(searchQuery),
      refresh: fetch,
      enabled: Boolean(searchQuery),
    });
    return {
      graph_loading: loading,
      graph_error: error,
      graph_data: data ?? { graph: [], totalItems: 0 },
    };
  },

  GetLinkedinAnalytics(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.linkedin',
      params: searchQuery,
      queryFn: () => this.getLinkedinAnalytics(searchQuery),
      refresh: fetch,
      enabled: Boolean(searchQuery),
    });
    return {
      linkedin_loading: loading,
      linkedin_error: error,
      linkedin_data: data ?? {},
    };
  },

  GetNotifications(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.notifications',
      params: searchQuery,
      queryFn: () => this.getNotifications(searchQuery),
      refresh: fetch,
    });
    return {
      notifications_loading: loading,
      notifications_error: error,
      notifications_data: data ?? { notifications: [], totalItems: 0 },
    };
  },

  GetCampaignsByMember(searchQuery, fetch) {
    const enabled = searchQuery?.skipFetch !== true && Boolean(searchQuery?.userId);
    const { data, loading, error } = useServiceQuery({
      name: 'dashboard.campaignsByMember',
      params: searchQuery,
      queryFn: () => this.getCampaignsByMember(searchQuery),
      refresh: fetch,
      enabled: enabled,
    });
    // Nothing to fetch without a member: settled and empty rather than loading.
    return {
      campaigns_loading: enabled && loading,
      campaigns_error: error,
      campaigns_data: enabled ? data?.data : undefined,
    };
  },

  async getKpiAnalytics({ campaignId = '', memberUserId = '', status = '', dateRange = '' }) {
    const queryString = buildAnalyticsQueryString({ status, dateRange, campaignId, memberUserId });
    let res = await Fetch.get(`${this._url}/get-kpi-analytics?${queryString}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getGraphData({ status = '', dateRange = '', campaignId = '', memberUserId = '', period = '' }) {
    const queryString = buildAnalyticsQueryString({ status, dateRange, campaignId, memberUserId, period });

    return withCache(`get-graph-analytics?${queryString}`, async () => {
      let res = await Fetch.get(`${this._url}/get-graph-analytics?${queryString}`);
      if (res.status >= 200 && res.status < 300) {
        res = await res.json();
        return adaptGraphResponse(res);
      }
      const { message } = await res.json();
      throw new Error(message ?? 'Something went wrong');
    });
  },

  async getLinkedinAnalytics({ status = '', dateRange = '', campaignId = '', memberUserId = '', period = '' }) {
    const queryString = buildAnalyticsQueryString({ status, dateRange, campaignId, memberUserId, period });

    return withCache(`get-linkedin-analytics?${queryString}`, async () => {
      let res = await Fetch.get(`${this._url}/get-linkedin-analytics?${queryString}`);
      if (res.status >= 200 && res.status < 300) {
        res = await res.json();
        return res;
      }
      const { message } = await res.json();
      throw new Error(message ?? 'Something went wrong');
    });
  },

  async getNotifications({ page = 1, itemsPerPage = 10, getAll = false }) {
    let res = await Fetch.get(
      `${this._url}/get-notifications?page=${page}&itemsPerPage=${itemsPerPage}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getTopCampaigns({ dateRange = '' } = {}) {
    const queryString = buildAnalyticsQueryString({ dateRange });

    return withCache(`get-top-campaigns?${queryString}`, async () => {
      let res = await Fetch.get(`${this._url}/get-top-campaigns?${queryString}`);
      if (res.status >= 200 && res.status < 300) {
        res = await res.json();
        return adaptTopCampaignsResponse(res);
      }
      const { message } = await res.json();
      throw new Error(message ?? 'Something went wrong');
    });
  },

  async getLatestActivities({ dateRange = '' } = {}) {
    const queryString = buildAnalyticsQueryString({ dateRange });

    return withCache(`get-last-activities?${queryString}`, async () => {
      let res = await Fetch.get(`${this._url}/get-last-activities?${queryString}`);
      if (res.status >= 200 && res.status < 300) {
        res = await res.json();
        return adaptActivitiesResponse(res);
      }
      const { message } = await res.json();
      throw new Error(message ?? 'Something went wrong');
    });
  },

  async markAsread() {
    let res = await Fetch.patch(`${this._url}/mark-notifications-read`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },


  async getCampaignsByMember({ userId = '', page = 1, itemsPerPage = 10, searchText = '', getAll = false, campaignType = '' }) {
    let url = `${this._url}/get-campaigns-by-member?userId=${userId}&page=${page}&itemsPerPage=${itemsPerPage}&searchText=${encodeURIComponent(searchText)}&getAll=${getAll}`;
    if (campaignType) url += `&campaignType=${campaignType}`;
    let res = await Fetch.get(url);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
};
export default dashboardService;
